import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Gauge, Search } from 'lucide-react'
import api from '../services/api'

// Encerrantes das bombas (Combustível → Saldos): encerrante de cada abastecimento do CTA, por bomba, com os furos destacados.

interface Linha {
  empcod: number; ctaId: number; dpsnum: number | null; deposito: string; bomba: string; data: string; hora: string | null
  volume: number; encerranteInicial: number; encerranteFinal: number; finalAnterior: number | null; diferenca: number | null
  veicod: string; placa: string; litrosLancados: number | null
  divergencia: '' | 'furo' | 'sobreposicao' | 'litros'
}
interface Resposta { tabelaCriada: boolean; linhas: Linha[]; bombas: { bomba: string; deposito: string }[]; depositos: { codigo: string; nome: string }[] }

const TOL = 0.05
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const br = (s: string) => s.split('-').reverse().join('/')
const fmt = (v: number | null | undefined, c = 3) => v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const sinal = (v: number) => `${v > 0 ? '+' : ''}${fmt(v)}`

const TEXTO_DIV: Record<Exclude<Linha['divergencia'], ''>, string> = {
  furo: 'Saída sem registro',
  sobreposicao: 'Encerrante voltou',
  litros: 'Litros lançados ≠ bomba',
}

const th: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#4b6478', textTransform: 'uppercase', textAlign: 'left', background: '#f4f8fb', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '0.42rem 0.6rem', fontSize: '0.79rem', borderBottom: '1px solid #eef2f6', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
const dir: React.CSSProperties = { ...td, textAlign: 'right' }
const card: React.CSSProperties = { background: '#fff', border: '1px solid #dfe7ef', borderRadius: 12, boxShadow: '0 8px 24px -14px rgba(22,54,83,.25)', overflow: 'hidden' }

export default function EncerrantesBombas() {
  const [sp] = useSearchParams()
  const hoje = new Date()
  const [dataIni, setDataIni] = useState(sp.get('dataIni') || iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)))
  const [dataFim, setDataFim] = useState(sp.get('dataFim') || iso(hoje))
  const [deposito, setDeposito] = useState('')
  const [bomba, setBomba] = useState('')
  const [soDivergencias, setSoDivergencias] = useState(sp.get('divergencias') === '1')
  const [dados, setDados] = useState<Resposta | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')

  async function pesquisar() {
    setErro(''); setCarregando(true)
    try {
      const r = await api.get('/combustivel/encerrantes', { params: { dataIni, dataFim, deposito, bomba } })
      setDados(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar os encerrantes.')
    } finally { setCarregando(false) }
  }
  useEffect(() => { pesquisar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const linhas = dados?.linhas ?? []
  const divergentes = linhas.filter(l => l.divergencia)
  const litrosFuro = divergentes.filter(l => l.divergencia === 'furo').reduce((s, l) => s + (l.diferenca || 0), 0)

  // Resumo por bomba e dia: giro da bomba (último final − primeiro inicial) x litros do CTA x litros lançados
  const resumo = useMemo(() => {
    const m = new Map<string, { data: string; bomba: string; deposito: string; ini: number; fim: number; volume: number; lancados: number; qtd: number; divergencias: number }>()
    for (const l of linhas) {
      const k = `${l.bomba}|${l.data}`
      const g = m.get(k) ?? { data: l.data, bomba: l.bomba, deposito: l.deposito, ini: l.encerranteInicial, fim: l.encerranteFinal, volume: 0, lancados: 0, qtd: 0, divergencias: 0 }
      g.ini = Math.min(g.ini, l.encerranteInicial); g.fim = Math.max(g.fim, l.encerranteFinal)
      g.volume += l.volume; g.lancados += l.litrosLancados ?? 0; g.qtd++; if (l.divergencia) g.divergencias++
      m.set(k, g)
    }
    return [...m.values()].sort((a, b) => a.data.localeCompare(b.data) || a.bomba.localeCompare(b.bomba))
  }, [linhas])

  const exibidas = soDivergencias ? linhas.filter(l => l.divergencia) : linhas
  const depositos = dados?.depositos ?? []
  const bombasFiltro = (dados?.bombas ?? []).filter(b => !deposito || b.deposito === deposito)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', maxWidth: 1500 }}>
      {/* Filtros */}
      <div style={{ ...card, padding: '0.9rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.6rem 0.9rem', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 'auto' }}>
          <span style={{ width: 36, height: 36, borderRadius: 9, background: '#e8f1f8', color: '#1b3f7b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Gauge size={19} /></span>
          <div>
            <div style={{ fontWeight: 800, color: '#153f66' }}>Encerrantes das Bombas</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Leitura da bomba em cada abastecimento do CTA Plus — o inicial de um tem que ser o final do anterior</div>
          </div>
        </div>
        <div className="field" style={{ width: 140 }}><label>Data inicial</label><input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} /></div>
        <div className="field" style={{ width: 140 }}><label>Data final</label><input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} /></div>
        <div className="field" style={{ width: 230 }}><label>Depósito</label>
          <select value={deposito} onChange={e => { setDeposito(e.target.value); setBomba('') }}>
            <option value="">Todos</option>
            {depositos.map(d => <option key={d.codigo} value={d.codigo}>{d.codigo}{d.nome ? ` - ${d.nome}` : ""}</option>)}
          </select>
        </div>
        <div className="field" style={{ width: 210 }}><label>Bomba</label>
          <select value={bomba} onChange={e => setBomba(e.target.value)}>
            <option value="">{bombasFiltro.length ? "Todas" : "Nenhuma bomba registrada ainda"}</option>
            {bombasFiltro.map(b => <option key={b.bomba} value={b.bomba}>{b.bomba}{b.deposito ? ` (${b.deposito})` : ''}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600, color: '#334155', paddingBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={soDivergencias} onChange={e => setSoDivergencias(e.target.checked)} /> Só divergências
        </label>
        <button className="btn btn-primary" onClick={pesquisar} disabled={carregando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Search size={15} /> {carregando ? 'Pesquisando...' : 'Pesquisar'}
        </button>
      </div>

      {erro && <div style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b' }}>{erro}</div>}
      {dados && !dados.tabelaCriada && (
        <div style={{ fontSize: '0.82rem', padding: '0.7rem 0.9rem', borderRadius: 8, background: '#fff7ec', color: '#8b5b00', border: '1px solid #f3d29a' }}>
          Esta base ainda não grava os encerrantes (falta a migration 047).
        </div>
      )}

      {/* Alerta de divergência */}
      {dados?.tabelaCriada && linhas.length > 0 && (divergentes.length > 0 ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '0.85rem 1rem', borderRadius: 10, background: '#fff3f4', border: '1px solid #ffd4da', borderLeft: '5px solid #db3545' }}>
          <AlertTriangle size={22} color="#db3545" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: '0.82rem', color: '#7f1d1d', lineHeight: 1.5 }}>
            <b style={{ fontSize: '0.9rem' }}>{divergentes.length} {divergentes.length === 1 ? 'divergência' : 'divergências'} de encerrante no período</b>
            <div>
              {(['furo', 'sobreposicao', 'litros'] as const).map(t => {
                const n = divergentes.filter(l => l.divergencia === t).length
                return n ? <span key={t} style={{ marginRight: 14 }}>• {TEXTO_DIV[t]}: <b>{n}</b>{t === 'furo' ? ` (${fmt(litrosFuro)} L)` : ''}</span> : null
              })}
            </div>
            <div style={{ color: '#9f3a46', fontSize: '0.74rem', marginTop: 2 }}>
              "Saída sem registro" = a bomba girou mais do que os abastecimentos do CTA entre um e outro (abastecimento não registrado, excluído ou bomba usada fora do CTA).
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '0.7rem 1rem', borderRadius: 10, background: '#f0f9f4', border: '1px solid #c7ead5', color: '#166534', fontSize: '0.82rem', fontWeight: 600 }}>
          <CheckCircle2 size={18} /> Nenhuma divergência: os encerrantes de todas as bombas fecham com os abastecimentos do período.
        </div>
      ))}

      {/* Resumo por bomba e dia */}
      {resumo.length > 0 && (
        <div style={card}>
          <div style={{ padding: '0.7rem 1rem', fontWeight: 800, color: '#153f66', fontSize: '0.88rem', borderBottom: '1px solid #eef2f7' }}>Resumo por bomba e dia</div>
          <div style={{ overflowX: 'auto', maxHeight: 320 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead><tr>
                <th style={th}>Data</th><th style={th}>Depósito</th><th style={th}>Bomba</th><th style={{ ...th, textAlign: 'right' }}>Abast.</th>
                <th style={{ ...th, textAlign: 'right' }}>Encerrante inicial</th><th style={{ ...th, textAlign: 'right' }}>Encerrante final</th>
                <th style={{ ...th, textAlign: 'right' }}>Litros pela bomba</th><th style={{ ...th, textAlign: 'right' }}>Litros CTA</th>
                <th style={{ ...th, textAlign: 'right' }}>Litros lançados</th><th style={{ ...th, textAlign: 'right' }}>Diferença</th>
              </tr></thead>
              <tbody>
                {resumo.map(g => {
                  const giro = g.fim - g.ini, dif = Math.round((giro - g.volume) * 1000) / 1000
                  const ruim = g.divergencias > 0 || Math.abs(dif) > TOL
                  return (
                    <tr key={`${g.bomba}|${g.data}`} style={{ background: ruim ? '#fff6f7' : undefined }}>
                      <td style={td}>{br(g.data)}</td><td style={td}>{g.deposito || '—'}</td><td style={td}>{g.bomba}</td><td style={dir}>{g.qtd}</td>
                      <td style={dir}>{fmt(g.ini)}</td><td style={dir}>{fmt(g.fim)}</td><td style={{ ...dir, fontWeight: 700 }}>{fmt(giro)}</td>
                      <td style={dir}>{fmt(g.volume)}</td><td style={dir}>{fmt(g.lancados)}</td>
                      <td style={{ ...dir, fontWeight: 800, color: ruim ? '#db3545' : '#16884a' }}>{Math.abs(dif) > TOL ? sinal(dif) : 'OK'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Abastecimentos */}
      {dados?.tabelaCriada && (
        <div style={card}>
          <div style={{ padding: '0.7rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eef2f7' }}>
            <span style={{ fontWeight: 800, color: '#153f66', fontSize: '0.88rem' }}>Abastecimentos por bomba</span>
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{exibidas.length} {exibidas.length === 1 ? 'abastecimento' : 'abastecimentos'}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1150 }}>
              <thead><tr>
                <th style={th}>Bomba</th><th style={th}>Data</th><th style={th}>Hora</th><th style={th}>ID CTA</th><th style={th}>Veículo</th><th style={th}>Placa</th>
                <th style={{ ...th, textAlign: 'right' }}>Litros CTA</th><th style={{ ...th, textAlign: 'right' }}>Litros lançados</th>
                <th style={{ ...th, textAlign: 'right' }}>Encerrante inicial</th><th style={{ ...th, textAlign: 'right' }}>Encerrante final</th>
                <th style={{ ...th, textAlign: 'right' }}>Dif. p/ anterior</th><th style={th}>Situação</th>
              </tr></thead>
              <tbody>
                {exibidas.length === 0 && (
                  <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>
                    {carregando ? 'Carregando...' : soDivergencias ? 'Nenhuma divergência no período. 👍' : 'Nenhum encerrante gravado no período.'}
                  </td></tr>
                )}
                {exibidas.map(l => {
                  const cor = l.divergencia ? '#db3545' : undefined
                  return (
                    <tr key={`${l.empcod}|${l.ctaId}`} style={{ background: l.divergencia ? '#fff3f4' : undefined }}>
                      <td style={td}><b>{l.bomba}</b> <span style={{ color: '#94a3b8' }}>{l.deposito}</span></td>
                      <td style={td}>{br(l.data)}</td><td style={td}>{l.hora || '—'}</td><td style={{ ...td, color: '#64748b' }}>{l.ctaId}</td>
                      <td style={td}>{l.veicod || <span style={{ color: '#b45309' }} title="Sem lançamento no dpsdes (oficina, na correção ou excluído)">sem lançamento</span>}</td>
                      <td style={td}>{l.placa || '—'}</td>
                      <td style={dir}>{fmt(l.volume)}</td>
                      <td style={{ ...dir, color: l.divergencia === 'litros' ? cor : undefined, fontWeight: l.divergencia === 'litros' ? 800 : undefined }}>{fmt(l.litrosLancados)}</td>
                      <td style={dir}>{fmt(l.encerranteInicial)}</td><td style={dir}>{fmt(l.encerranteFinal)}</td>
                      <td style={{ ...dir, fontWeight: 800, color: l.diferenca != null && Math.abs(l.diferenca) > TOL ? '#db3545' : '#94a3b8' }}
                        title={l.finalAnterior != null ? `Final do abastecimento anterior: ${fmt(l.finalAnterior)}` : 'Sem abastecimento anterior nos últimos 31 dias'}>
                        {l.diferenca == null ? '—' : Math.abs(l.diferenca) > TOL ? `${sinal(l.diferenca)} L` : '0'}
                      </td>
                      <td style={td}>
                        {l.divergencia
                          ? <span style={{ padding: '2px 8px', borderRadius: 10, background: '#ffe3e6', color: '#c62e3d', fontWeight: 800, fontSize: '0.7rem' }}>{TEXTO_DIV[l.divergencia]}</span>
                          : <span style={{ color: '#16884a', fontWeight: 700, fontSize: '0.72rem' }}>OK</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
